import { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

export interface VedicYoga {
  id: string;
  name: string;
  type: string;
  category: string;
  group: string;
  isPositive: boolean;
  status: 'Confirmed' | 'Possible' | 'Needs Dasha Confirmation' | string;
  needsDashaConfirmation?: boolean;
  source: string;
  strength: 'Very Strong' | 'Strong' | 'Medium' | 'Weak' | string;
  formation: string;
  effects: string[];
  remedies: string[];
}

interface UseVedicYogasOptions {
  enabled?: boolean;
  token?: string | null;
}

export function useVedicYogas(chartData: any, options: UseVedicYogasOptions = {}) {
  const enabled = options.enabled !== false;
  const [yogas, setYogas] = useState<VedicYoga[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !chartData?.planets || !(chartData?.ascendant?.sign || chartData?.houses?.ascendant?.sign)) {
      setYogas([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchYogas = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/vedic/yogas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
          },
          body: JSON.stringify(chartData),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to detect yogas.');
        }

        if (!cancelled) {
          setYogas(data.yogas || []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to detect yogas.');
          setYogas([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchYogas();

    return () => {
      cancelled = true;
    };
  }, [chartData, enabled, options.token]);

  return {
    yogas,
    loading,
    error,
  };
}
